import { css } from '@emotion/react';
import type { NavLinkProps } from 'react-router';
import { NavLink } from 'react-router';

import { useCallout } from '@adflow/frontend/ui-common';
import { su, theme } from '@adflow/frontend/ui-theme';

export const navItemStyles = {
	navLink: css(su.textSm, {
		display: 'flex',
		alignItems: 'center',
		paddingLeft: '1.25rem',
		paddingRight: '1.25rem',
		textDecoration: 'none',
		paddingTop: '2px',
		borderBottom: '2px solid transparent',
		color: theme.colors.palettes.primary.dark,
		transition: 'all 0.2s ease-in-out',
		whiteSpace: 'nowrap',

		'&:not([data-disabled=true]):hover': {
			color: theme.colors.palettes.primary.main,
			textShadow: '0px 0px 4px #0000001f',
		},

		'&.active': {
			color: '#505050',
			textShadow: '0px 0px 4px #00000040',
			borderBottom: `2px solid ${theme.colors.palettes.primary.main}`,
		},

		'&[data-disabled=true]': css({
			color: theme.colors.text.faint,
			fontWeight: '300',
			cursor: 'default',
		}),
	}),
	navLinkNarrow: css({
		paddingLeft: '0.75rem',
		paddingRight: '0.75rem',
	}),
};

export interface NavItemProps extends Omit<NavLinkProps, 'activeClassName'> {
	disabled?: boolean;
	disabledCalloutText?: string;
	narrow?: boolean;
	alternativeAction?: () => void;
}

export function NavItem(props: NavItemProps) {
	const {
		children,
		disabled,
		disabledCalloutText,
		narrow,
		onClick,
		alternativeAction,
		...other
	} = props;

	const { callout, setShowViewCallout } = useCallout({
		text: disabledCalloutText ?? '',
	});

	return (
		<>
			<NavLink
				css={[navItemStyles.navLink, narrow && navItemStyles.navLinkNarrow]}
				// Using data-disabled instead to allow for cursor events to propagate
				data-disabled={disabled}
				data-testid={
					typeof children === 'string' ? `header-${children}` : undefined
				}
				onClick={(e) => {
					if (disabled) {
						setShowViewCallout(e);
						e.preventDefault();
					} else if (alternativeAction) {
						alternativeAction();
						e.preventDefault();
					}
					onClick?.(e);
				}}
				{...other}
			>
				{children}
			</NavLink>
			{callout}
		</>
	);
}
